import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Rocket, Shield, Activity, AlertTriangle, CheckCircle, XCircle,
  Users, DollarSign, BarChart3, RefreshCw, ArrowRight, Loader2,
  Zap, Eye, FileText, Lock,
} from 'lucide-react';
import api from '../../lib/api';
import { useAppDialog } from '@/hooks/useAppDialog';

const STAGE_CONFIG = {
  stage_1: { label: 'Stage 1', name: 'Core Operations', color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: Users },
  stage_2: { label: 'Stage 2', name: 'Controlled Financials', color: 'bg-amber-100 text-amber-800 border-amber-300', icon: DollarSign },
  stage_3: { label: 'Stage 3', name: 'Full Platform', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Rocket },
};

export default function SoftLaunchPage() {
  const { confirm } = useAppDialog();
  const [pilotStage, setPilotStage] = useState(null);
  const [monitoring, setMonitoring] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageChanging, setStageChanging] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [stageRes, monRes] = await Promise.all([
        api.get('/rollout/pilot-stage'),
        api.get('/rollout/live-monitoring'),
      ]);
      if (stageRes.data?.data) setPilotStage(stageRes.data.data);
      if (monRes.data?.data) setMonitoring(monRes.data.data);
    } catch (e) {
      console.error('Failed to load soft launch data', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStageChange = async (newStage) => {
    const stageLabel = STAGE_CONFIG[newStage]?.name || newStage;
    const ok = await confirm({
      title: `Move to ${stageLabel}?`,
      description: 'This will adjust feature-flag kill switches for the new stage. Any flag that depends on the previous stage will be affected.',
      confirmLabel: 'Move stage',
      variant: 'warning',
      testId: 'softlaunch-stage-confirm',
    });
    if (!ok) return;
    setStageChanging(true);
    try {
      const res = await api.put('/rollout/pilot-stage', { is_pilot: true, stage: newStage });
      if (res.data?.data) {
        toast.success(res.data.data.message);
        fetchData();
      }
    } catch (e) {
      toast.error('Failed to change stage');
    }
    setStageChanging(false);
  };

  const handlePilotToggle = async (enabled) => {
    const action = enabled ? 'activate' : 'deactivate';
    const ok = await confirm({
      title: `${enabled ? 'Activate' : 'Deactivate'} pilot mode?`,
      description: enabled
        ? 'Pilot mode applies stage-based kill switches and limits the user pool. Recommended only for test environments.'
        : 'Disabling pilot mode removes the kill switches and exposes the platform to all users at the current stage.',
      confirmLabel: enabled ? 'Activate' : 'Deactivate',
      variant: enabled ? 'warning' : 'danger',
      testId: 'softlaunch-pilot-toggle-confirm',
    });
    if (!ok) return;
    setStageChanging(true);
    try {
      const stage = pilotStage?.pilot_stage || 'stage_1';
      const res = await api.put('/rollout/pilot-stage', { is_pilot: enabled, stage });
      if (res.data?.data) {
        toast.success(`Pilot mode ${action}d`);
        fetchData();
      }
    } catch (e) {
      toast.error(`Failed to ${action} pilot mode`);
    }
    setStageChanging(false);
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  const isPilot = pilotStage?.is_pilot || false;
  const currentStage = pilotStage?.pilot_stage || 'stage_1';
  const stageCfg = STAGE_CONFIG[currentStage] || STAGE_CONFIG.stage_1;
  const StageIcon = stageCfg.icon;

  const health = monitoring?.health || {};
  const sys = monitoring?.system || {};
  const flags = monitoring?.feature_flags || {};
  const flagEntries = Object.entries(flags);
  const enabledCount = flagEntries.filter(([, v]) => v.enabled && !v.kill_switch).length;
  const killedCount = flagEntries.filter(([, v]) => v.kill_switch).length;

  return (
    <div className="space-y-6 p-1" data-testid="soft-launch-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" data-testid="soft-launch-title">
            <Rocket className="h-6 w-6" /> Soft Launch Control
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Controlled pilot rollout with staged feature activation</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Pilot Status Banner */}
      <Card className={isPilot ? 'border-2 border-primary/30' : ''} data-testid="pilot-status-card">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${isPilot ? 'bg-primary/10' : 'bg-muted'}`}>
                <Shield className={`h-6 w-6 ${isPilot ? 'text-primary' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-medium">Pilot Mode</h2>
                  {isPilot ? (
                    <Badge className={`${stageCfg.color} border`} data-testid="current-stage-badge">
                      <StageIcon className="h-3 w-3 mr-1" /> {stageCfg.label}: {stageCfg.name}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {isPilot ? 'Feature access controlled by pilot stage' : 'All features available to all users'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isPilot && (
                <Select value={currentStage} onValueChange={handleStageChange} disabled={stageChanging}>
                  <SelectTrigger className="w-[200px]" data-testid="stage-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stage_1">Stage 1: Core Operations</SelectItem>
                    <SelectItem value="stage_2">Stage 2: Controlled Financials</SelectItem>
                    <SelectItem value="stage_3">Stage 3: Full Platform</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Switch
                checked={isPilot}
                onCheckedChange={handlePilotToggle}
                disabled={stageChanging}
                data-testid="pilot-toggle"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <MetricCard label="Active Users" value={sys.active_users || 0} icon={Users} />
        <MetricCard label="Participants" value={sys.total_participants || 0} icon={Activity} />
        <MetricCard label="Features Active" value={enabledCount} icon={Zap} accent="text-emerald-600" />
        <MetricCard label="Kill Switches" value={killedCount} icon={Lock} accent={killedCount > 0 ? 'text-red-600' : 'text-emerald-600'} />
        <MetricCard label="Errors (24h)" value={health.recent_errors_24h || 0} icon={AlertTriangle} accent={health.recent_errors_24h > 0 ? 'text-amber-600' : 'text-emerald-600'} />
        <MetricCard label="Failed Invoices" value={health.failed_invoices || 0} icon={DollarSign} accent={health.failed_invoices > 0 ? 'text-red-600' : 'text-emerald-600'} />
      </div>

      <Separator />

      {/* Stage Progression */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="stage-progression">
        {Object.entries(STAGE_CONFIG).map(([key, cfg]) => {
          const isActive = isPilot && currentStage === key;
          const isPast = isPilot && Object.keys(STAGE_CONFIG).indexOf(currentStage) > Object.keys(STAGE_CONFIG).indexOf(key);
          const stageDef = pilotStage?.stages?.[key] || {};
          const Icon = cfg.icon;
          return (
            <Card key={key} className={`${isActive ? 'border-2 border-primary ring-1 ring-primary/20' : isPast ? 'border-emerald-200 bg-emerald-50/30' : ''}`} data-testid={`stage-card-${key}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {cfg.label}: {cfg.name}
                  </CardTitle>
                  {isActive && <Badge className="bg-primary text-primary-foreground text-xs">CURRENT</Badge>}
                  {isPast && <Badge variant="outline" className="text-emerald-600 border-emerald-600 text-xs"><CheckCircle className="h-3 w-3 mr-1" />Done</Badge>}
                </div>
                <CardDescription className="text-xs">{stageDef.description || cfg.name}</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {stageDef.enabled_flags?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs text-muted-foreground mb-1">Enabled:</p>
                    <div className="flex flex-wrap gap-1">
                      {stageDef.enabled_flags.slice(0, 4).map(f => (
                        <Badge key={f} variant="secondary" className="text-xs">{f.replace(/_enabled$/, '').replace(/_/g, ' ')}</Badge>
                      ))}
                      {stageDef.enabled_flags.length > 4 && <Badge variant="outline" className="text-xs">+{stageDef.enabled_flags.length - 4}</Badge>}
                    </div>
                  </div>
                )}
                {stageDef.blocked_flags?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Blocked:</p>
                    <div className="flex flex-wrap gap-1">
                      {stageDef.blocked_flags.slice(0, 3).map(f => (
                        <Badge key={f} variant="destructive" className="text-xs">{f.replace(/_enabled$/, '').replace(/_/g, ' ')}</Badge>
                      ))}
                      {stageDef.blocked_flags.length > 3 && <Badge variant="outline" className="text-xs">+{stageDef.blocked_flags.length - 3}</Badge>}
                    </div>
                  </div>
                )}
                {key === 'stage_3' && <p className="text-xs text-emerald-600 mt-1">All features unlocked</p>}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Feature Flags Status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2"><Eye className="h-4 w-4" /> Feature Status Overview</CardTitle>
          <CardDescription>Current state of all feature flags</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {flagEntries.map(([key, val]) => (
              <div key={key} className="flex items-center justify-between p-2 rounded-md border text-sm" data-testid={`flag-status-${key}`}>
                <span className="truncate mr-2">{key.replace(/_enabled$/, '').replace(/_/g, ' ')}</span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {val.kill_switch ? (
                    <Badge variant="destructive" className="text-xs">KILLED</Badge>
                  ) : val.enabled ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">ON</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">OFF</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Stage History */}
      {pilotStage?.stage_history?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4" /> Stage History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pilotStage.stage_history.slice(-10).reverse().map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-sm py-1 border-b last:border-0">
                  <Badge variant="outline" className="text-xs">{h.from_stage}</Badge>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <Badge variant="outline" className="text-xs">{h.to_stage}</Badge>
                  <span className="text-xs text-muted-foreground ml-auto">{new Date(h.changed_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, accent }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{label}</p>
          <Icon className="h-4 w-4 text-muted-foreground/50" />
        </div>
        <p className={`text-xl font-semibold mt-1 tabular-nums ${accent || ''}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
